import xmlToJson from './xml-to-json';
import isUndefined from './is-undefined';
import guid from './guid';
import Logger from '../../utils/logger';
import { METADATA_TYPE_PLAYLIST } from '../constants';

/**
 * Parses an HH:MM:SS timecode to seconds
 *
 * @param {string} timecode
 * @return {number}
 */
const parseTimecode = function (timecode) {
  const parts = timecode.split(':').reverse();
  const seconds = parseInt(parts.shift(), 10);
  const minutes = parts[0] ? parseInt(parts.shift(), 10) : 0;
  const hours = parts[0] ? parseInt(parts.shift(), 10) : 0;

  return seconds + (minutes * 60) + (hours * 60 * 60);
};

/**
 * Parses an RSS feed response
 * @todo forward any parser errors to the callback
 * @param {string}   data     Data to be parsed as RSS
 * @param {function} callback Function called when the parser is done
 */
export default function parseRss (data, callback) {
  Logger.debug('[RssParser] Parsing RSS');

  try {
    const parser = new DOMParser();
    const rss = parser.parseFromString(data.trim(), 'text/xml');

    const parsererrors = rss.getElementsByTagName('parsererror');

    if (parsererrors.length > 0) {
      throw new Error('Failed to parse rss feed contents');
    }

    const feed = xmlToJson(rss.documentElement);

    const playlistMetadata = {
      title: feed.channel.title['#text'],
      contentType: METADATA_TYPE_PLAYLIST,
      icons: []
    };

    let channelSmallIcon = null;
    let channelMediumIcon = null;
    let channelLargeIcon = null;

    if (!isUndefined(feed.channel['ionofm:coverart']) && feed.channel['ionofm:coverart']['@attributes'].href) {
      channelSmallIcon = feed.channel['ionofm:coverart']['@attributes'].href;
      playlistMetadata.icons.push({
        size: 'small',
        url: channelSmallIcon
      });
    }

    if (!isUndefined(feed.channel['ionofm:thumbnail']) && feed.channel['ionofm:thumbnail']['@attributes'].href) {
      channelMediumIcon = feed.channel['ionofm:thumbnail']['@attributes'].href;
      playlistMetadata.icons.push({
        size: 'medium',
        url: channelMediumIcon
      });
    }

    if (!isUndefined(feed.channel.image) && feed.channel.image.url['#text']) {
      channelLargeIcon = feed.channel.image.url['#text'];
      playlistMetadata.icons.push({
        size: 'large',
        url: channelLargeIcon
      });
    } else if (!isUndefined(feed.channel['itunes:image']) && feed.channel['itunes:image']['@attributes'].href) {
      channelLargeIcon = feed.channel['itunes:image']['@attributes'].href;
      playlistMetadata.icons.push({
        size: 'large',
        url: channelLargeIcon
      });
    }

    const playlistItems = feed.channel.item.reduce(function (items, item) {
      if (isUndefined(item.enclosure)) {
        Logger.warn('[RssParser] Ignoring RSS feed item because it has no enclosure');
        return items;
      }

      if (isUndefined(item.title)) {
        Logger.warn('[RssParser] Ignoring RSS feed item because it has no title');
        return items;
      }

      // Parse metadata
      const metadata = {
        // uid comes from `guid`
        uid: item.guid['#text'] || guid(),

        // title comes from `title`, `itunes:title`
        title: item.title['#text'],

        // description comes from `description`, `itunes:summary`
        description: !isUndefined(item['itunes:summary'])
          ? item['itunes:summary']['#text']
          : item.description['#text'],

        // image comes from ionofm:thumbnail`, `ionofm:coverart` or `itunes:image`
        icons: [],

        // link comes from `link`, `atom:link`
        url: !isUndefined(item.link)
          ? item.link['#text']
          : '',

        // duration comes from `itunes:duration`
        duration: !isUndefined(item['itunes:duration'])
          ? parseTimecode(item['itunes:duration']['#text'])
          : 0,

        // publishDate comes from `pubDate`
        publishDate: !isUndefined(item.pubDate)
          ? item.pubDate['#text']
          : null,

        // creator comes from `itunes:author`
        creator: !isUndefined(item['itunes:author'])
          ? item['itunes:author']['#text']
          : ''
      };

      if (!isUndefined(item['ionofm:coverart']) && item['ionofm:coverart']['@attributes'].href) {
        metadata.icons.push({
          size: 'small',
          url: item['ionofm:coverart']['@attributes'].href
        });
      } else if (channelSmallIcon) {
        metadata.icons.push({
          size: 'small',
          url: channelSmallIcon
        });
      }

      if (!isUndefined(item['ionofm:thumbnail']) && item['ionofm:thumbnail']['@attributes'].href) {
        metadata.icons.push({
          size: 'medium',
          url: item['ionofm:thumbnail']['@attributes'].href
        });
      } else if (channelMediumIcon) {
        metadata.icons.push({
          size: 'medium',
          url: channelMediumIcon
        });
      }

      if (!isUndefined(item.image) && item.image.url['#text']) {
        metadata.icons.push({
          size: 'large',
          url: item.image.url['#text']
        });
      } else if (!isUndefined(item['itunes:image']) && item['itunes:image']['@attributes'].href) {
        metadata.icons.push({
          size: 'large',
          url: item['itunes:image']['@attributes'].href
        });
      } else if (channelLargeIcon) {
        metadata.icons.push({
          size: 'large',
          url: channelLargeIcon
        });
      }

      // Add language if present
      if (!isUndefined(feed.channel.language['#text'])) {
        metadata.language = feed.channel.language['#text'];
      }

      // Parse audio tracks
      const audioTracks = [
        {
          url: item.enclosure['@attributes'].url,
          filesize: parseInt(item.enclosure['@attributes'].length, 10),
          mime: item.enclosure['@attributes'].type
        }
      ];

      metadata.download = item.enclosure['@attributes'].url;

      // Push item on to the playlist
      items.push({
        metadata: metadata,
        audio: audioTracks
      });

      return items;
    }, []);

    const playlist = {
      metadata: playlistMetadata,
      items: playlistItems
    };

    callback(false, playlist);
  } catch (e) {
    callback(e, false);
  }
}
